import React from "react"
import { cn } from "@/lib/utils"

function Skeleton({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
	return <div className={cn("skeleton-shimmer rounded-md", className)} {...props} />
}

/** Skeleton for dashboard stat cards (row of cards with icon + number) */
function DashboardCardsSkeleton() {
	return (
		<div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
			{Array.from({ length: 5 }).map((_, i) => (
				<div key={i} className="bg-white rounded-xl border border-gray-100 p-5 space-y-3">
					<div className="flex items-center gap-3">
						<Skeleton className="w-10 h-10 rounded-full" />
						<Skeleton className="h-4 w-20" />
					</div>
					<Skeleton className="h-8 w-12" />
				</div>
			))}
		</div>
	)
}

/** Skeleton for a list of report rows */
function ReportListSkeleton({ rows = 5 }: { rows?: number }) {
	return (
		<div className="space-y-3">
			{Array.from({ length: rows }).map((_, i) => (
				<div key={i} className="bg-white rounded-lg border border-gray-100 p-4 flex items-center gap-4">
					<Skeleton className="w-10 h-10 rounded-lg" />
					<div className="flex-1 space-y-2">
						<Skeleton className="h-4 w-2/3" />
						<Skeleton className="h-3 w-1/3" />
					</div>
					<Skeleton className="h-6 w-16 rounded-full" />
				</div>
			))}
		</div>
	)
}

/** Skeleton for the new report form page */
function NewReportSkeleton() {
	return (
		<div className="max-w-4xl mx-auto space-y-8">
			<div className="space-y-2">
				<Skeleton className="h-8 w-64" />
				<Skeleton className="h-4 w-96" />
			</div>
			<div className="bg-white rounded-xl border border-gray-100 p-6 space-y-6">
				<Skeleton className="h-5 w-40" />
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					{Array.from({ length: 4 }).map((_, i) => (
						<div key={i} className="space-y-2">
							<Skeleton className="h-4 w-24" />
							<Skeleton className="h-10 w-full rounded-md" />
						</div>
					))}
				</div>
			</div>
			<div className="bg-white rounded-xl border border-gray-100 p-6 space-y-4">
				<Skeleton className="h-5 w-48" />
				<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
					{Array.from({ length: 3 }).map((_, i) => (
						<div key={i} className="border border-gray-100 rounded-lg p-4 space-y-3">
							<Skeleton className="h-4 w-3/4" />
							<Skeleton className="h-3 w-full" />
							<Skeleton className="h-3 w-2/3" />
						</div>
					))}
				</div>
			</div>
			<div className="flex justify-end gap-3">
				<Skeleton className="h-10 w-24 rounded-md" />
				<Skeleton className="h-10 w-32 rounded-md" />
			</div>
		</div>
	)
}

/** Generic page skeleton with title + content blocks */
function PageSkeleton() {
	return (
		<div className="space-y-6">
			<div className="space-y-2">
				<Skeleton className="h-8 w-48" />
				<Skeleton className="h-4 w-72" />
			</div>
			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
				{Array.from({ length: 6 }).map((_, i) => (
					<div key={i} className="bg-white rounded-xl border border-gray-100 p-5 space-y-3">
						<Skeleton className="h-4 w-3/4" />
						<Skeleton className="h-3 w-full" />
						<Skeleton className="h-3 w-1/2" />
					</div>
				))}
			</div>
		</div>
	)
}

export { Skeleton, DashboardCardsSkeleton, ReportListSkeleton, NewReportSkeleton, PageSkeleton }
